import axios, { AxiosInstance } from 'axios';
import {
  DashboardQueues,
  AcquisitionMetrics,
  ChatMetrics,
  ResourceMetrics,
  UsersListResponse,
  UserDetail,
  ChatDetail,
  BillingQuotaConfigResponse,
  PlanConfig,
  PlanQuotaConfigItem,
  TokenPricingItem,
  PlanBillingMappingItem,
} from '../types';

class AdminApiService {
  private api: AxiosInstance;

  constructor() {
    const serverUrl = (import.meta as any).env?.VITE_SERVER_URL;
    const baseURL = serverUrl ? `${serverUrl}/api/v1` : '/api/v1';

    this.api = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid, redirect to login
          localStorage.removeItem('adminToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Dashboard endpoints
  async getQueueMetrics(startDate?: string, endDate?: string): Promise<DashboardQueues> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.api.get('/admin/dashboard/queues', { params });
    return response.data;
  }

  async getAcquisitionMetrics(startDate?: string, endDate?: string): Promise<AcquisitionMetrics> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.api.get('/admin/dashboard/acquisition', { params });
    return response.data;
  }

  async getChatMetrics(startDate?: string, endDate?: string): Promise<ChatMetrics> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.api.get('/admin/dashboard/chats', { params });
    return response.data;
  }

  async getResourceMetrics(startDate?: string, endDate?: string): Promise<ResourceMetrics> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.api.get('/admin/dashboard/resources', { params });
    return response.data;
  }

  // Users endpoints
  async getUsersList(
    page: number = 1,
    limit: number = 50,
    search?: string
  ): Promise<UsersListResponse> {
    const params: any = { page, limit };
    if (search) params.search = search;

    const response = await this.api.get('/admin/users', { params });
    return response.data;
  }

  async getUserDetail(userId: string): Promise<{ user: UserDetail }> {
    const response = await this.api.get(`/admin/users/${userId}`);
    return response.data;
  }

  // Chats endpoints
  async getChatDetail(chatId: string): Promise<ChatDetail> {
    const response = await this.api.get(`/admin/chats/${chatId}`);
    return response.data;
  }

  async getBillingQuotaConfig(): Promise<BillingQuotaConfigResponse> {
    const response = await this.api.get('/admin/config/billing-quota');
    return response.data;
  }

  async savePlan(payload: Partial<PlanConfig> & Pick<PlanConfig, 'code' | 'displayName' | 'monthlyPriceUsd' | 'isActive' | 'sortOrder'>): Promise<PlanConfig> {
    const response = await this.api.post('/admin/config/plans', payload);
    return response.data;
  }

  async saveQuotaConfig(payload: Partial<PlanQuotaConfigItem> & Pick<PlanQuotaConfigItem, 'planId' | 'monthlyLimitUsd' | 'windowDivisor' | 'periodType' | 'isActive'>): Promise<PlanQuotaConfigItem> {
    const response = await this.api.post('/admin/config/quotas', payload);
    return response.data;
  }

  async saveTokenPricing(payload: Partial<TokenPricingItem> & Pick<TokenPricingItem, 'provider' | 'model' | 'tokenType' | 'pricePer1mTokensUsd' | 'isActive'>): Promise<TokenPricingItem> {
    const response = await this.api.post('/admin/config/token-pricing', payload);
    return response.data;
  }

  async saveBillingMapping(payload: Partial<PlanBillingMappingItem> & Pick<PlanBillingMappingItem, 'planId' | 'provider' | 'environment' | 'paddlePriceId' | 'isActive'>): Promise<PlanBillingMappingItem> {
    const response = await this.api.post('/admin/config/billing-mappings', payload);
    return response.data;
  }
}

export const adminApiService = new AdminApiService();
export default adminApiService;

